import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TypingArea from '@/components/TypingArea';
import WaveformViz from '@/components/WaveformViz';
import MetricChip from '@/components/MetricChip';
import StatusBadge from '@/components/StatusBadge';
import type { BiometricFeatures, KeystrokeEvent } from '@/types/keystroke';

/** Default prompts for sessions */
const PROMPTS = [
  'O sol nasceu devagar sobre as montanhas, iluminando o vale com tons dourados e alaranjados que refletiam na superficie calma do lago.',
  'A tecnologia avanca em ritmo acelerado, transformando completamente a maneira como nos comunicamos, trabalhamos e vivemos no mundo moderno.',
  'Cada pessoa possui um padrao unico de digitacao que funciona como uma impressao digital biometrica, impossivel de ser replicada com precisao.',
  'A seguranca digital depende de multiplas camadas de protecao, desde senhas fortes ate autenticacao biometrica avancada baseada em comportamento.',
  'Quando digitamos naturalmente sem pensar nas teclas individuais nosso cerebro cria padroes ritmicos que sao tao unicos quanto nossa caligrafia manuscrita.',
];

/**
 * TypingSession — Core typing screen with timer, live waveform, and metrics.
 */
export default function TypingSession() {
  const navigate = useNavigate();
  const [promptIndex] = useState(() => {
    // Get session index from enrollment state or default
    const stored = sessionStorage.getItem('hks_session_index');
    return stored ? parseInt(stored, 10) % PROMPTS.length : 0;
  });
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [keystrokeCount, setKeystrokeCount] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const startTimeRef = useRef<number>(0);

  const prompt = PROMPTS[promptIndex];

  // Timer
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleKeystroke = useCallback(
    (count: number, data: number[]) => {
      setKeystrokeCount(count);
      setWaveformData(data);

      // Start timer on first keystroke
      if (count === 1 && !timerRef.current) {
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 100);
      }
    },
    [],
  );

  const handleComplete = useCallback(
    (events: KeystrokeEvent[], features: BiometricFeatures, typedText: string) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = undefined;
      }
      setIsComplete(true);

      // Store result for SessionResult page
      sessionStorage.setItem(
        'hks_session_result',
        JSON.stringify({ events: events.length, features, typedText }),
      );

      // Navigate to result after a brief delay
      setTimeout(() => {
        navigate('/session-result', {
          state: { features, typedText, events: events.length },
        });
      }, 800);
    },
    [navigate],
  );

  // Format elapsed time as MM:SS
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-8">
      {/* Top bar */}
      <div className="flex items-center justify-between max-w-typing mx-auto w-full mb-8">
        <button
          onClick={() => navigate('/enrollment')}
          className="text-sm text-txt-muted hover:text-txt-primary transition-colors font-sans"
        >
          &larr; Voltar
        </button>

        <div className="flex items-center gap-3">
          <StatusBadge
            label={isComplete ? 'Concluido' : keystrokeCount > 0 ? 'Digitando' : 'Aguardando'}
            variant={isComplete ? 'success' : keystrokeCount > 0 ? 'brand' : 'neutral'}
            pulse={keystrokeCount > 0 && !isComplete}
          />
        </div>

        <div className="text-sm font-mono text-txt-muted">
          {formatTime(elapsedTime)}
        </div>
      </div>

      {/* Session info */}
      <div className="text-center mb-6 max-w-typing mx-auto">
        <h2 className="text-lg font-semibold text-txt-primary font-sans mb-1">
          Sessao de Digitacao
        </h2>
        <p className="text-xs text-txt-muted font-sans">
          Digite o texto abaixo de forma natural. Nao se preocupe com velocidade.
        </p>
      </div>

      {/* Main typing area */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-typing mx-auto w-full">
        <TypingArea
          promptText={prompt}
          onComplete={handleComplete}
          onKeystroke={handleKeystroke}
          autoFocus={true}
          showLiveMetrics={true}
        />

        {/* Waveform */}
        <div className="mt-8 w-full">
          <WaveformViz
            data={waveformData}
            width={700}
            height={80}
            color="#7c5cfc"
            maxPoints={30}
            animate={true}
            label="Ritmo de digitacao"
          />
        </div>

        {/* Live metrics */}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <MetricChip
            label="Teclas"
            value={keystrokeCount}
            color="#4fc3f7"
          />
          <MetricChip
            label="Tempo"
            value={formatTime(elapsedTime)}
            color="#e8e8e8"
          />
          {waveformData.length > 0 && (
            <>
              <MetricChip
                label="Fluidez"
                value={`${Math.round(
                  waveformData.reduce((a, b) => a + b, 0) / waveformData.length,
                )}ms`}
                color="#7c5cfc"
              />
              <MetricChip
                label="Ultimo"
                value={`${Math.round(waveformData[waveformData.length - 1] || 0)}ms`}
                color="#e2b714"
              />
            </>
          )}
        </div>
      </div>

      {/* Completion overlay */}
      {isComplete && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-bg-primary/80 backdrop-blur-sm animate-fade-in">
          <div className="text-center animate-slide-up">
            <div className="text-5xl mb-4">{'\u2713'}</div>
            <h3 className="text-xl font-bold text-accent-success font-sans mb-2">
              Sessao Concluida!
            </h3>
            <p className="text-sm text-txt-muted font-sans">
              Analisando sua assinatura de digitacao...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
